import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BofuTabDocument,
  BofuTabFormsEmpresa,
  DocumentResponse,
  ExpedienteTabItem,
  GarantiasVehicularInmobiliariaResponse,
  PersonaStructure,
} from "@interfaces/response";
import { BOFU_TABS_CONFIG } from "@helpers/constantes";

export type ExpedienteEmpresaState = ExpedienteTabItem & {
  doc_solicitud_credito: DocumentResponse | undefined;
  forms: BofuTabFormsEmpresa;
};

export type PersonaExpediente = PersonaStructure & {
  showPersonalForm: boolean;
  showIdentityValidation: boolean;
  showBankForm: boolean;
};

export interface ExpedientePersonasState extends ExpedienteTabItem {
  persons: PersonaExpediente[];
  documentsByPerson: Record<string, BofuTabDocument[]>;
}

export interface ExpedienteGarantiaState extends ExpedienteTabItem {
  items: {
    guarantee: GarantiasVehicularInmobiliariaResponse;
    documents: Array<BofuTabDocument>;
  }[];
}

export interface ExpedienteState {
  tabsReady: boolean;
  dictamen: ExpedienteTabItem;
  empresa: ExpedienteEmpresaState;
  garantia: ExpedienteGarantiaState;
  personas: ExpedientePersonasState;
}

const makeTab = (code: string, order: number): ExpedienteTabItem => ({
  code: code as ExpedienteTabItem["code"],
  order,
  is_active: false,
  is_disabled: true,
  tab_status: undefined,
  documents: [],
});

const initialState: ExpedienteState = {
  tabsReady: false,
  dictamen: makeTab(BOFU_TABS_CONFIG.tab_dictamen.code, 1),
  empresa: {
    ...makeTab(BOFU_TABS_CONFIG.tab_empresa.code, 2),
    doc_solicitud_credito: undefined,
    forms: {
      solicitud_credito: { enabled: true, config: {} },
      referencias_personales: true,
      informacion_bancaria: true,
    },
  },
  garantia: {
    ...makeTab(BOFU_TABS_CONFIG.tab_garantia.code, 3),
    items: [],
  },
  personas: {
    ...makeTab(BOFU_TABS_CONFIG.tab_personas.code, 4),
    persons: [],
    documentsByPerson: {},
  },
};

export const expedienteSlice = createSlice({
  name: "expediente",
  initialState,
  reducers: {
    setExpedienteTabsReady(state, action: PayloadAction<boolean>) {
      state.tabsReady = action.payload;
    },
    setExpedienteDictamen(state, action: PayloadAction<ExpedienteTabItem>) {
      state.dictamen = action.payload;
    },
    setExpedienteGarantia(state, action: PayloadAction<ExpedienteGarantiaState>) {
      state.garantia = action.payload;
    },
    setExpedientePersonas(state, action: PayloadAction<ExpedientePersonasState>) {
      state.personas = action.payload;
    },
    setExpedienteEmpresa(state, action: PayloadAction<ExpedienteEmpresaState>) {
      state.empresa = action.payload;
    },
    setExpedienteDictamenDoc(
      state,
      action: PayloadAction<{
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update" | null;
        valueIndex?: number;
      }>,
    ) {
      const { document, newValue, valueAction, valueIndex } = action.payload;
      const idx = state.dictamen.documents.findIndex(
        (doc) => doc.file_category_code === document.file_category_code && doc.relationship === document.relationship,
      );
      if (idx === -1) return;
      if (document.multiple) {
        if (valueAction === "add") state.dictamen.documents[idx].list_values.push(newValue);
        else if (valueAction === "update" && valueIndex !== undefined)
          state.dictamen.documents[idx].list_values[valueIndex] = newValue;
        else if (valueAction === "remove" && valueIndex !== undefined)
          state.dictamen.documents[idx].list_values.splice(valueIndex, 1);
      } else {
        state.dictamen.documents[idx].file_value = newValue;
      }
    },
    setExpedienteEmpresaDoc(
      state,
      action: PayloadAction<{
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update" | null;
        valueIndex?: number;
      }>,
    ) {
      const { document, newValue, valueAction, valueIndex } = action.payload;
      const idx = state.empresa.documents.findIndex(
        (doc) => doc.file_category_code === document.file_category_code && doc.relationship === document.relationship,
      );
      if (idx === -1) return;
      if (document.multiple) {
        if (valueAction === "add") state.empresa.documents[idx].list_values.push(newValue);
        else if (valueAction === "update" && valueIndex !== undefined)
          state.empresa.documents[idx].list_values[valueIndex] = newValue;
        else if (valueAction === "remove" && valueIndex !== undefined)
          state.empresa.documents[idx].list_values.splice(valueIndex, 1);
      } else {
        state.empresa.documents[idx].file_value = newValue;
      }
    },
    resetExpediente() {
      return initialState;
    },
  },
});

export const {
  setExpedienteTabsReady,
  setExpedienteDictamen,
  setExpedienteEmpresa,
  setExpedienteGarantia,
  setExpedientePersonas,
  setExpedienteDictamenDoc,
  setExpedienteEmpresaDoc,
  resetExpediente,
} = expedienteSlice.actions;

export default expedienteSlice.reducer;
